import { useCallback, useEffect, useRef } from "react";

import { useNavigate } from "react-router-dom";
import { useQueryClient, useQuery, QueryFilters } from "@tanstack/react-query";

import qs from "query-string";

import mergeWith from "lodash/mergeWith";
import filter from "lodash/filter";
import isObject from "lodash/isObject";

import { useAppContext } from "..";
import { useSchedulesContext } from "../../schedules";
import { getApiUrl } from "../../../lib/common/getApiUrl";

const useBoardStatus = () => {
  const queryClient = useQueryClient();

  const { screenId, clientId, config } = useAppContext();

  const { scheduleScreensaver } = useSchedulesContext();

  const navigate = useNavigate();

  const socketRef = useRef<WebSocket | null>(null);

  const onMessage = useCallback(async (event: MessageEvent<any>) => {
    const { data: rawMessage } = event;

    try {
      const message: ScreenMessage = JSON.parse(rawMessage);

      if (!(message && message.action)) {
        return;
      }

      const predicate: QueryFilters["predicate"] = ({ queryKey }) => {
        if (!(Array.isArray(queryKey) && queryKey.length)) {
          return false;
        }

        const [key, params] = queryKey;

        if (key !== "digitalReception") {
          return false;
        }

        return "id" in params && params.id === screenId;
      };

      switch (message.action) {
        case "restart": {
          window.location.href = qs.stringifyUrl({
            url: `${window.location.origin}/client/${clientId}/board/${screenId}`,
            query: config,
          });

          break;
        }

        case "refresh": {
          const { digitalReception } = await queryClient.fetchQuery<{
            digitalReception: IDigitalReception;
          }>(["digitalReception", { id: screenId }]);

          if (config.preview) {
            break;
          }

          const sleepSchedules = filter(
            digitalReception?.schedules,
            (item) => item.type === "sleep" && !item.schedule.disabled,
          );

          if (sleepSchedules.length > 0) {
            scheduleScreensaver(sleepSchedules);
          }

          break;
        }

        case "update-digitalReception": {
          const { data } = message;

          queryClient.setQueriesData<{ digitalReception: IDigitalReception }>(
            {
              predicate,
            },
            (prev) => {
              // prettier-ignore
              if (!(prev && prev.digitalReception && data && data.digitalReception)) {
                return data;
              }

              const { digitalReception: prevDigitalReception, ...rest } = prev;
              const { digitalReception: partialDigitalReception } = data as {
                digitalReception: Partial<IDigitalReception>;
              };

              const newOptions =
                partialDigitalReception?.settings?.layout?.multiWayfinding
                  ?.wayfindingOptions;

              const updated = structuredClone(prevDigitalReception);

              if (Array.isArray(newOptions)) {
                updated.settings = {
                  ...(updated.settings ?? {}),
                  layout: {
                    ...(updated.settings?.layout ?? {}),
                    // @ts-ignore
                    multiWayfinding: {
                      ...(updated.settings?.layout?.multiWayfinding ?? {}),
                      wayfindingOptions: newOptions,
                    },
                  },
                };
              }

              const sanitizedPartial = structuredClone(partialDigitalReception);

              if (
                sanitizedPartial?.settings?.layout?.multiWayfinding
                  ?.wayfindingOptions
              ) {
                delete sanitizedPartial.settings.layout.multiWayfinding
                  .wayfindingOptions;

                if (
                  Object.keys(sanitizedPartial.settings.layout.multiWayfinding)
                    .length === 0
                ) {
                  delete sanitizedPartial.settings.layout.multiWayfinding;
                }

                if (
                  Object.keys(sanitizedPartial.settings.layout).length === 0
                ) {
                  delete sanitizedPartial.settings.layout;
                }

                if (Object.keys(sanitizedPartial.settings).length === 0) {
                  delete sanitizedPartial.settings;
                }
              }

              const digitalReception = mergeWith(
                updated,
                sanitizedPartial,
                (obj, src) => {
                  if (Array.isArray(obj)) return src;
                  if (isObject(obj)) return mergeWith(obj, src);
                  return src;
                },
              );

              return {
                ...rest,
                digitalReception,
              };
            },
          );

          break;
        }

        case "document-scan": {
          const { data } = message;

          if (!data) {
            return;
          }

          if (data.status !== "valid") {
            navigate("/access/checkin/manual", {
              state: {
                documentScanStatus: "invalid",
              },
            });

            break;
          }

          const state: Record<string, any> = {};

          if (data.fullName) {
            state.fullname = {
              id: data.fullName.id,
              value: data.fullName.value,
            };
          }

          if (data.documentNumber) {
            state.documentNumber = {
              id: data.documentNumber.id,
              value: data.documentNumber.value,
            };
          }

          if (data.nationality) {
            state.nationality = {
              id: data.nationality.id,
              value: data.nationality.value,
            };
          }

          if (data.gender) {
            state.gender = { id: data.gender.id, value: data.gender.value };
          }

          if (data.personalNumber) {
            state.personalNumber = {
              id: data.personalNumber.id,
              value: data.personalNumber.value,
            };
          }

          if (Object.keys(state).length > 0) {
            navigate("/access/checkin/manual", { state });
          }

          break;
        }

        case "get-printers": {
          const { requestId } = message.data || {};

          let printers: string[] = [];

          try {
            // @ts-ignore
            if (nw?.Window) {
              // @ts-ignore
              const win = nw.Window.get();

              win.getPrinters((list: any[]) => {
                printers = list.map((p) => p.printerName);

                socketRef.current?.send(
                  JSON.stringify({
                    action: "printers-response",
                    data: { requestId, printers },
                  }),
                );
              });
              return;
            }
          } catch {
            //
          }

          socketRef.current?.send(
            JSON.stringify({
              action: "printers-response",
              data: { requestId, printers: [] },
            }),
          );

          break;
        }

        default: {
          break;
        }
      }
    } catch {
      //
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", onMessage);

    return () => window.removeEventListener("message", onMessage);
  }, []);

  return useQuery(
    [`network/board/${screenId}`],
    ({ queryKey: [url] }) => {
      return new Promise((resolve, reject) => {
        const apiUrl = getApiUrl();

        const baseURL = apiUrl.replace(/(http)(s)?\:\/\//, "ws$2://");

        const socket = new WebSocket(`${baseURL}/${url}/`);

        if (socket.readyState === WebSocket.OPEN) {
          socketRef.current = socket;
          resolve(true);

          return;
        }

        socket.onopen = function (e) {
          const previous = socketRef.current;

          socketRef.current = socket;

          if (previous && previous !== socket) {
            previous.onclose = null;
            previous.onerror = null;
            previous.onmessage = null;
            previous.close();
          }

          return resolve(e);
        };

        socket.onerror = function (error) {
          queryClient.invalidateQueries([url], { exact: true });

          return reject(error);
        };

        socket.onmessage = function (event) {
          onMessage(event);
        };

        socket.onclose = () => {
          queryClient.invalidateQueries([url], { exact: true });

          return reject(new Error("Socket close. Retry"));
        };
      });
    },
    {
      retry: true,
      retryDelay: 5000,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      enabled: config?.connect && !!screenId,
    },
  );
};

export default useBoardStatus;
